import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import styles from "./styles.module.css";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogFooter,
} from "../../ui/dialog";
import {
  getCarePlanReviews,
  addCarePlanReview,
} from "../../../services/patient/carePlan";
import { handleApiError } from "../../../utils/handleApiError";
import { formatDate } from "../../../utils/helper";
import toast from "react-hot-toast";
import Button from "../../button-v1";
import Spinner from "../../spinner";

const CareplanReview = ({ open, setOpen, careplanId, actionEnable }) => {
  const queryClient = useQueryClient();
  const [reviewText, setReviewText] = useState("");
  const textareaRef = useRef(null);
  const lastMessageRef = useRef(null);

  // Get careplanId from props or sessionStorage
  const currentCareplanId = careplanId || sessionStorage.getItem("careplan_id");

  const reviewsQuery = useQuery({
    queryKey: ["carePlanReviews", currentCareplanId],
    queryFn: () => getCarePlanReviews(currentCareplanId),
    enabled: !!currentCareplanId && open,
  });

  const reviewsData = reviewsQuery.data;

  const addReviewMutation = useMutation(
    (reviewData) => addCarePlanReview(currentCareplanId, reviewData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["carePlanReviews"] });
        queryClient.invalidateQueries({ queryKey: ["notifications"] });

        setReviewText("");
        toast.success("Review added successfully");
      },
      onError: (error) => {
        handleApiError(error);
        toast.error("Failed to add review");
      },
    }
  );

  const handleAddReview = () => {
    const trimmedReview = reviewText.trim();
    if (!trimmedReview) {
      toast.error("Please enter a review");
      return;
    }

    if (!currentCareplanId) {
      toast.error("Care plan ID is missing");
      return;
    }

    addReviewMutation.mutate({
      review: trimmedReview,
    });
  };

  const reviewMessages = Array.isArray(reviewsData)
    ? [...reviewsData].reverse()
    : [];

  useEffect(() => {
    if (!open) return;
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({
        block: "end",
        behavior: "smooth",
      });
    }
  }, [reviewsData, open]);

  return (
    <Dialog open={open} setOpen={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Careplan Reviews</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <section className={styles.container}>
            {reviewsQuery.isLoading ? (
              <Spinner />
            ) : reviewMessages.length === 0 ? (
              <p>No reviews yet</p>
            ) : (
              reviewMessages?.map((msg, index) => (
                <section
                  key={msg.id || index}
                  className={styles.msg}
                  ref={
                    index === reviewMessages.length - 1 ? lastMessageRef : null
                  }
                >
                  <section className={styles.msg_stats}>
                    <p>
                      {msg?.reviewed_by}{" "}
                      {msg.is_sender && <strong>(You)</strong>}
                    </p>
                    <p>{formatDate(msg?.created_at)}</p>
                  </section>
                  <main className={styles.msg_text}>{msg?.review}</main>
                </section>
              ))
            )}
          </section>
        </DialogBody>
        {actionEnable && (
          <DialogFooter>
            <section className={styles.input_container}>
              <textarea
                ref={textareaRef}
                className={styles.text_area}
                placeholder="write here..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                disabled={addReviewMutation.isPending}
              />
            </section>
            <section className={styles.action_container}>
              <Button
                onClick={handleAddReview}
                disabled={addReviewMutation.isPending || !reviewText.trim()}
                loading={addReviewMutation.isLoading}
              >
                Add
              </Button>
              <Button
                variant="ghost"
                size="default"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </section>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CareplanReview;
